import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';

const HeroSection = () => {
  const [daysUntilDeadline, setDaysUntilDeadline] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const getNextTaxDeadline = () => {
      const today = new Date();
      const currentYear = today.getFullYear();

      // Try April 15 of the current year first
      let deadline = new Date(currentYear, 3, 15); // Month is 0-indexed, so 3 = April

      // If April 15 of this year has already passed, use April 15 of next year
      if (today > deadline) {
        deadline = new Date(currentYear + 1, 3, 15);
      }

      return deadline;
    };

    const updateCountdown = () => {
      const deadline = getNextTaxDeadline();
      const today = new Date();
      const diffTime = deadline.getTime() - today.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      setDaysUntilDeadline(diffDays > 0 ? diffDays : 0);
    };

    updateCountdown();
    setTimeout(() => setIsVisible(true), 100);

    // Refresh countdown daily at midnight
    const now = new Date();
    const msUntilMidnight =
      new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1).getTime() - now.getTime();

    const midnightTimeout = setTimeout(() => {
      updateCountdown();
      // After first midnight refresh, set a daily interval
      const dailyInterval = setInterval(updateCountdown, 24 * 60 * 60 * 1000);
      return () => clearInterval(dailyInterval);
    }, msUntilMidnight);

    return () => clearTimeout(midnightTimeout);
  }, []);

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden">
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: 'url(https://public.readdy.ai/ai/img_res/ac270edf940540d5a35f84535e4999b1.jpg)',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/70"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-32 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="text-white">
            {/* Business Name - Eye-catching hero branding */}
            <div
              className={`mb-6 transition-all duration-1000 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
            >
              <div className="inline-flex items-center gap-2 mb-4 px-4 py-1.5 rounded-full border border-orange-500/40 bg-orange-500/10 backdrop-blur-sm">
                <span className="w-2 h-2 rounded-full bg-orange-500 animate-pulse"></span>
                <span className="text-orange-400 font-sans text-sm font-medium tracking-wide uppercase">Jacksonville, Florida</span>
              </div>

              <h1 className="font-montserrat font-bold leading-none mb-2">
                <span className="block text-5xl lg:text-7xl tracking-tight">
                  <span className="text-white">Bold</span>{' '}
                  <span className="text-orange-500">Plans</span>
                </span>
                <span className="block text-5xl lg:text-7xl tracking-tight mt-1">
                  <span
                    className="relative inline-block text-white"
                  >
                    Advisory
                    <span className="absolute -bottom-2 left-0 w-full h-1 bg-gradient-to-r from-orange-500 via-orange-400 to-transparent rounded-full"></span>
                  </span>
                </span>
              </h1>
            </div>

            {/* Tagline */}
            <div
              className={`mb-8 transition-all duration-1000 delay-300 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
            >
              <p className="text-xl lg:text-2xl font-playfair italic text-gray-200 mb-2">
                Expert Tax Solutions You Can Trust
              </p>
              <p className="text-base text-gray-400 font-sans max-w-lg">
                Strategic Planning &bull; Year-Round Support &bull; Maximum Refunds
              </p>
            </div>

            <div
              className={`flex flex-col sm:flex-row gap-4 mb-8 transition-all duration-1000 delay-500 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
            >
              <Link
                to="/contact"
                className="inline-flex items-center justify-center bg-orange-500 text-white px-8 py-4 rounded-full font-sans font-semibold hover:bg-orange-600 transition-all whitespace-nowrap cursor-pointer shadow-lg shadow-orange-500/25"
              >
                Schedule Consultation
              </Link>
              <Link
                to="/client-portal"
                className="inline-flex items-center justify-center bg-transparent text-white px-8 py-4 rounded-full font-sans font-semibold border-2 border-white/30 hover:bg-white hover:text-navy-800 transition-all whitespace-nowrap cursor-pointer"
              >
                Access Client Portal
              </Link>
            </div>

            <div
              className={`flex flex-wrap gap-6 text-sm font-sans transition-all duration-1000 delay-700 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
            >
              <div className="flex items-center gap-2">
                <i className="ri-shield-check-line text-orange-500 text-xl"></i>
                <span>IRS Certified</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-time-line text-orange-500 text-xl"></i>
                <span>10+ Years Experience</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-file-list-3-line text-orange-500 text-xl"></i>
                <span>1,000+ Returns Filed</span>
              </div>
            </div>
          </div>

          <div
            className={`space-y-6 transition-all duration-1000 delay-500 ${
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'
            }`}
          >
            <div className="bg-white/10 backdrop-blur-md rounded-3xl p-8 border border-white/20">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 flex items-center justify-center bg-orange-500 rounded-full">
                  <i className="ri-calendar-line text-white text-2xl"></i>
                </div>
                <div>
                  <p className="text-white/70 text-sm font-sans">Tax Deadline Countdown</p>
                  <p className="text-white font-montserrat font-bold text-3xl">{daysUntilDeadline} Days</p>
                </div>
              </div>
              <p className="text-white/80 text-sm font-sans">
                Don&apos;t wait until the last minute. Schedule your consultation today and maximize your refund.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
                <div className="w-10 h-10 flex items-center justify-center bg-orange-500/20 rounded-full mb-3">
                  <i className="ri-money-dollar-circle-line text-orange-500 text-xl"></i>
                </div>
                <p className="text-white font-montserrat font-bold text-lg">Max Refunds</p>
                <p className="text-white/70 text-sm font-sans">Guaranteed</p>
              </div>

              <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
                <div className="w-10 h-10 flex items-center justify-center bg-orange-500/20 rounded-full mb-3">
                  <i className="ri-shield-star-line text-orange-500 text-xl"></i>
                </div>
                <p className="text-white font-montserrat font-bold text-lg">Audit Defense</p>
                <p className="text-white/70 text-sm font-sans">Included Free</p>
              </div>

              <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
                <div className="w-10 h-10 flex items-center justify-center bg-orange-500/20 rounded-full mb-3">
                  <i className="ri-customer-service-2-line text-orange-500 text-xl"></i>
                </div>
                <p className="text-white font-montserrat font-bold text-lg">24/7 Support</p>
                <p className="text-white/70 text-sm font-sans">Year-Round</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
